import { Text } from "@chakra-ui/react";
import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGlossary } from "src/hooks/useGlossary";
import * as Format from "src/services/format";
import { GetDashboardFormResponsesOverTimeResponse } from "src/types/formTemplate/v2";
import * as RD from "src/types/remoteData";
import { ChartTooltip } from "./ChartTooltip";
import { DashboardChart } from "./DashboardChart";

const ASPECT_RATIO = 4;

type Props = {
  remoteData: RD.RemoteData<unknown, GetDashboardFormResponsesOverTimeResponse>;
  exportId?: string;
};
export const ResponseSubmissionsOverTime: React.FC<Props> = ({
  remoteData,
  exportId,
}) => {
  const data = React.useMemo(() => {
    return remoteData.map((response) => {
      const chartData = response.data
        .map((x) => {
          const [year, month] = x.month.split("-");
          if (!year || !month) {
            return null;
          }
          const date = new Date(
            Date.UTC(parseInt(year), parseInt(month) - 1, 1)
          );
          const monthLabel = date.toLocaleDateString("en-US", {
            month: "short",
            year: "2-digit",
            timeZone: "UTC",
          });
          return { name: monthLabel, value: x.count };
        })
        .filter(
          (item): item is { name: string; value: number } => item !== null
        );

      return [
        { name: " ", value: null },
        ...chartData,
        { name: " ", value: null },
      ];
    });
  }, [remoteData]);

  const { glossary } = useGlossary();

  return (
    <DashboardChart
      aspectRatio={ASPECT_RATIO}
      title="Cumulative submissions over time"
      caption={glossary`Form responses by submitted date`}
      data={data}
      exportId={exportId}
      exportFileName="response-submissions-over-time"
      filters={null}
      cardProps={{
        gridColumn: "span 2",
        sx: {
          "@media print": {
            gridColumn: "span 2 !important",
          },
        },
      }}
    >
      {(chartData) => (
        <AreaChart
          style={{
            width: "100%",
            aspectRatio: ASPECT_RATIO,
          }}
          responsive
          data={chartData}
          margin={{
            top: 5,
            right: 0,
            left: 0,
            bottom: 5,
          }}
        >
          <defs>
            <linearGradient id="responsesArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#45B37A" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#6ED8A0" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical />
          <XAxis dataKey="name" axisLine={false} />
          <YAxis allowDecimals={false} axisLine={true} />
          <Tooltip<number, string>
            content={({ active, payload, label }) => {
              const value = payload?.[0]?.value;
              if (active && payload && value) {
                return (
                  <ChartTooltip
                    accentColor="green"
                    title={glossary`Submitted`}
                    subtitle={label !== undefined ? String(label) : undefined}
                  >
                    <Text fontSize="0.9rem">
                      <strong>{Format.formatWholeNumber(value)}</strong>{" "}
                      {value === 1 ? "response" : "responses"}
                    </Text>
                  </ChartTooltip>
                );
              }
              return null;
            }}
          />
          <Area
            type="linear"
            dataKey="value"
            stroke="#45B37A"
            strokeWidth={2}
            fill="url(#responsesArea)"
            dot={{ r: 4, fill: "#45B37A", stroke: "#CFECDD", strokeWidth: 2 }}
            activeDot={{ r: 5 }}
            connectNulls={false}
          />
        </AreaChart>
      )}
    </DashboardChart>
  );
};
